import { opportunityConstant } from "../constants/opportunityConstant";
import { commentConstant } from "../constants/commentConstant";
import type { Opportunity } from "../../containers/Pipelines/types";

interface OpportunityState {
  opportunities: Opportunity[];
  loading: boolean;
  error: any | null;
}

interface OpportunityAction {
  type: string;
  data?: any;
  payload?: any;
  error?: any;
}

const initialState: OpportunityState = {
  opportunities: [],
  loading: false,
  error: null,
};

export default function opportunity(state = initialState, action: OpportunityAction): OpportunityState {
  switch (action.type) {
    // Get Opportunities by Pipeline
    case opportunityConstant.GET_OPPORTUNITIES_BY_PIPELINE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case opportunityConstant.GET_OPPORTUNITIES_BY_PIPELINE_S:
      return {
        ...state,
        opportunities: action.data?.data?.opportunities || [],
        loading: false,
      };

    case opportunityConstant.GET_OPPORTUNITIES_BY_PIPELINE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // Get Opportunities by Stage
    case opportunityConstant.GET_OPPORTUNITIES_BY_STAGE_R:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case opportunityConstant.GET_OPPORTUNITIES_BY_STAGE_S:
      return {
        ...state,
        opportunities: action.data?.data?.opportunities || [],
        loading: false,
      };

    case opportunityConstant.GET_OPPORTUNITIES_BY_STAGE_F:
      return {
        ...state,
        loading: false,
        error: action.error,
      };

    // Get Single Opportunity
    case opportunityConstant.GET_OPPORTUNITY_R:
      return {
        ...state,
        error: null,
      };

    case opportunityConstant.GET_OPPORTUNITY_S:
      return {
        ...state,
      };

    case opportunityConstant.GET_OPPORTUNITY_F:
      return {
        ...state,
        error: action.error,
      };

    // Create Opportunity
    case opportunityConstant.CREATE_OPPORTUNITY_R:
      return {
        ...state,
        error: null,
      };

    case opportunityConstant.CREATE_OPPORTUNITY_S: {
      const newOpportunity = action.data?.data?.opportunity;
      return {
        ...state,
        opportunities: newOpportunity ? [...state.opportunities, newOpportunity] : state.opportunities,
      };
    }

    case opportunityConstant.CREATE_OPPORTUNITY_F:
      return {
        ...state,
        error: action.error,
      };

    // Update Opportunity
    case opportunityConstant.UPDATE_OPPORTUNITY_R:
      return {
        ...state,
        error: null,
      };

    case opportunityConstant.UPDATE_OPPORTUNITY_S: {
      const updatedOpportunity = action.data?.data?.opportunity;
      return {
        ...state,
        opportunities: updatedOpportunity
          ? state.opportunities.map((o) =>
              o.id === updatedOpportunity.id ? updatedOpportunity : o
            )
          : state.opportunities,
      };
    }

    case opportunityConstant.UPDATE_OPPORTUNITY_F:
      return {
        ...state,
        error: action.error,
      };

    // Delete Opportunity
    case opportunityConstant.DELETE_OPPORTUNITY_R:
      return {
        ...state,
        error: null,
      };

    case opportunityConstant.DELETE_OPPORTUNITY_S: {
      const deletedId = action.data?.id;
      return {
        ...state,
        opportunities: state.opportunities.filter((o) => o.id !== deletedId),
      };
    }

    case opportunityConstant.DELETE_OPPORTUNITY_F:
      return {
        ...state,
        error: action.error,
      };

    // Update Opportunity Order (Drag & Drop)
    case opportunityConstant.UPDATE_OPPORTUNITY_ORDER_R:
      return {
        ...state,
        error: null,
      };

    case opportunityConstant.UPDATE_OPPORTUNITY_ORDER_S: {
      const updatedOpportunity = action.data?.data?.opportunity;
      return {
        ...state,
        opportunities: updatedOpportunity
          ? state.opportunities.map((o) =>
              o.id === updatedOpportunity.id ? updatedOpportunity : o
            )
          : state.opportunities,
      };
    }

    case opportunityConstant.UPDATE_OPPORTUNITY_ORDER_F:
      return {
        ...state,
        error: action.error,
      };

    // Optimistic update for drag & drop (before API call)
    case 'opportunity/UPDATE_OPTIMISTIC': {
      const { id, stage_id, order_index } = action.payload || {};
      return {
        ...state,
        opportunities: state.opportunities.map((opp) =>
          opp.id === id
            ? { ...opp, stage_id, order_index }
            : opp
        ),
      };
    }

    // Clear State
    case opportunityConstant.CLEAR_OPPORTUNITIES_STATE:
      return initialState;

    // Update comment count when comments are created/deleted
    // DON'T update the opportunities array to avoid interfering with drag & drop
    // The comment count badge will update when we fetch comments for the popover
    case commentConstant.CREATE_COMMENT_S: {
      const { opportunityId } = action.data || {};
      // Only update if the opportunity exists in state
      const oppIndex = state.opportunities.findIndex((o) => o.id === opportunityId);
      if (oppIndex === -1) return state;
      
      // Create new array with minimal mutation to preserve drag & drop state
      const newOpportunities = state.opportunities.map((opp, idx) => {
        if (idx === oppIndex) {
          // Only update comment_count, preserve everything else including stage_id
          return {
            ...opp,
            comment_count: (opp.comment_count || 0) + 1
          };
        }
        return opp; // Return same reference for unchanged items
      });
      
      return {
        ...state,
        opportunities: newOpportunities,
      };
    }

    case commentConstant.DELETE_COMMENT_S: {
      const { opportunityId } = action.data || {};
      // Only update if the opportunity exists in state
      const oppIndex = state.opportunities.findIndex((o) => o.id === opportunityId);
      if (oppIndex === -1) return state;
      
      // Create new array with minimal mutation to preserve drag & drop state
      const newOpportunities = state.opportunities.map((opp, idx) => {
        if (idx === oppIndex) {
          // Only update comment_count, preserve everything else including stage_id
          return {
            ...opp,
            comment_count: Math.max((opp.comment_count || 1) - 1, 0)
          };
        }
        return opp; // Return same reference for unchanged items
      });
      
      return {
        ...state,
        opportunities: newOpportunities,
      };
    }

    default:
      return state;
  }
}
